/**
 * Pure data transforms for dashboard widgets — mirror the copilot engine's
 * visualization builders (`ai_engine._build_visualization_data`) so the same
 * chart reads the same way on both surfaces.
 */

import type { CampaignPerformanceRow, ConversionEvent } from '@/types/marketing'
import type { WidgetDataSource } from '@/types/dashboard'
import type { BudgetSankeyData, SankeyLink, SankeyNode } from '../visualizations/BudgetSankey'
import type { CampaignComparisonData } from '../visualizations/CampaignComparison'
import type { JourneyEvent } from '../visualizations/JourneyTimeline'

/** One entry per distinct API fetch the dashboard can make. */
export type FetchKind =
  | 'overview'
  | 'campaigns'
  | 'campaignPerformance'
  | 'attribution'
  | 'timeSeries'
  | 'optimizationActions'
  | 'conversions'
  | 'audiences'

/** Exhaustive by construction: a widget data source without a fetch kind is a
 * type error, so a registry entry can never silently render with no data. */
const SOURCE_TO_FETCH: Record<WidgetDataSource, FetchKind> = {
  overview: 'overview',
  campaigns: 'campaigns',
  campaignPerformance: 'campaignPerformance',
  campaignHealth: 'campaignPerformance',
  spendRoas: 'campaignPerformance',
  attribution: 'attribution',
  timeSeries: 'timeSeries',
  optimizationActions: 'optimizationActions',
  conversions: 'conversions',
  audiences: 'audiences',
}

/** Dedupe widget data sources down to the distinct fetches they require. */
export function resolveFetchKinds(sources: Iterable<WidgetDataSource>): Set<FetchKind> {
  const kinds = new Set<FetchKind>()
  for (const source of sources) kinds.add(SOURCE_TO_FETCH[source])
  return kinds
}

const OUTCOME_LABELS = [
  ['conversations', 'Conversations'],
  ['leads', 'Leads'],
  ['purchases', 'Purchases'],
] as const

/** Budget → campaigns (spend) → outcomes, spend split proportionally to outcome
 * counts. Campaigns without outcomes flow to Conversations so the diagram stays
 * connected. Node colors are left to the chart's palette. */
export function buildSankeyData(rows: CampaignPerformanceRow[]): BudgetSankeyData {
  const nodes: SankeyNode[] = []
  const links: SankeyLink[] = []
  const nodeIds = new Set<string>(['Budget'])

  const addNode = (id: string) => {
    if (!nodeIds.has(id)) {
      nodeIds.add(id)
      nodes.push({ id })
    }
  }

  // Nivo Sankey throws on circular links and duplicate node ids; a campaign
  // named like a reserved node ("Budget", "Conversations", …) or a repeated
  // campaign name must never crash the chart.
  const pushLink = (source: string, target: string, value: number) => {
    if (source === target) return false
    links.push({ source, target, value })
    return true
  }

  for (const row of rows) {
    if (row.spend <= 0) continue
    if (!pushLink('Budget', row.name, row.spend)) continue
    addNode(row.name)

    const totalOutcomes = row.conversations + row.leads + row.purchases
    if (totalOutcomes > 0) {
      for (const [key, label] of OUTCOME_LABELS) {
        const count = row[key]
        if (count > 0 && row.name !== label) {
          addNode(label)
          pushLink(row.name, label, Math.round((row.spend * count) / totalOutcomes))
        }
      }
    } else if (row.name !== 'Conversations') {
      addNode('Conversations')
      pushLink(row.name, 'Conversations', row.spend)
    }
  }

  if (links.length === 0) return { nodes: [], links: [] }
  return { nodes: [{ id: 'Budget' }, ...nodes], links }
}

const JOURNEY_EVENT_CAP = 12

/** Recent conversion events as a timeline: Purchase → purchase, all other
 * conversion events → lead. Scoped to the dashboard date window when given,
 * sorted ascending, capped to the most recent 12. Values stay in cents
 * (JourneyTimeline formats them). */
export interface ComparisonBarData {
  data: CampaignComparisonData[]
  /** Truncated axis label → campaign id, so bar clicks can resolve back to a
   * campaign for cross-widget selection. Two names truncating identically
   * collide (last wins) — acceptable at SMB campaign counts. */
  labelToId: Map<string, string>
}

/** Bar-chart rows for the campaign comparison widget, plus the label→id map
 * its click handler needs. */
export function buildComparisonBarData(rows: CampaignPerformanceRow[]): ComparisonBarData {
  const data: CampaignComparisonData[] = []
  const labelToId = new Map<string, string>()
  for (const row of rows) {
    const label = row.name.length > 20 ? row.name.slice(0, 18) + '...' : row.name
    labelToId.set(label, row.campaignId)
    data.push({
      campaign: label,
      spend: row.spend,
      clicks: row.clicks,
      conversations: row.conversations,
      leads: row.leads,
      roas: row.spend > 0 ? Math.round(((row.purchases || 0) / row.spend) * 100) : 0,
    })
  }
  return { data, labelToId }
}

export function buildJourneyEvents(
  events: ConversionEvent[],
  opts?: { sinceDays?: number; now?: Date },
): JourneyEvent[] {
  const cutoff = opts?.sinceDays != null
    ? new Date((opts.now ?? new Date()).getTime() - opts.sinceDays * 24 * 60 * 60 * 1000)
    : null
  return events
    .filter((e) => !cutoff || new Date(e.eventTime) >= cutoff)
    .sort((a, b) => a.eventTime.localeCompare(b.eventTime))
    .slice(-JOURNEY_EVENT_CAP)
    .map((e) => ({
      type: e.eventName.toLowerCase() === 'purchase' ? ('purchase' as const) : ('lead' as const),
      label: e.eventName,
      date: e.eventTime,
      details: { status: e.status },
      value: e.value,
    }))
}
